import { useCallback } from 'react';
import { Zone } from 'src/graphql/generated/graphql';
import { ZoneCreationProperties, SwitchButtonValue } from '../types/zoneMapType';
import { FixtureAssignment } from '../allocation/computeFixtureAllocations';
import { validateDropZones } from '../allocation/validateDropZones';
import { useProgramConfig } from 'src/hooks/useProgramConfig';

interface PublishZoneMapParams {
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  fixtureAssignments: FixtureAssignment[];
  selectedDepartment: string;
  selectedProductGroup: string;
  selectedCategory: string;
  macroplanogramNameInput: string;
  store: string;
  timeStamp: number;
  copyToNewTimestamp: number;
  switchButtonValue: string | undefined;
  nextGenEnabled: boolean;
  productTaxonomy:
    | { categoryList?: { categoryId: string; categoryName: string }[]; subcategoryList?: unknown[] }
    | undefined;
  publishZone: (input: { input: unknown }) => Promise<{ publishZone: { error: { errorMessage: string } | null } }>;
  putZoneMapConfig: (input: { input: unknown }) => Promise<{ putZoneMapConfig: { zoneMapConfigId: string | null } }>;
  updateFloorplan?: (input: { input: unknown }) => Promise<unknown>;
  floorplanId?: string;
}

export function buildZonesToUpdate(
  zoneCreationProperties: Map<number, ZoneCreationProperties>,
  fixtureAssignments: FixtureAssignment[],
  selectedDepartment: string,
  isGfcPublish: boolean,
): Zone[] {
  if (isGfcPublish) {
    // GFC publish: every zone carries an explicit categoryId on a first-class
    // field of MacroplanogramEntry. Populating Zone.categoryId on every zone
    // is the signal the publish resolver uses to take the GFC path.
    return Array.from(zoneCreationProperties.values()).map((value) => ({
      name: value.zoneName,
      subcategoryIds: value.subcategoryIds,
      fixtureIds: value.fixtureIds,
      facings: value.facings,
      categoryId: value.categoryId,
    }));
  }

  // Fresh/WFM: percentage-based zone building
  const zones: Zone[] = [];
  for (const value of zoneCreationProperties.values()) {
    const allocations = (value.allocations ?? []).filter((a) => a.subcategoryId && a.percentage > 0);
    zones.push({
      name: value.zoneName,
      subcategoryIds: allocations.length > 0 ? allocations.map((a) => a.subcategoryId) : value.subcategoryIds,
      fixtureIds: value.fixtureIds,
      facings: value.facings,
      department: selectedDepartment || value.productGroup || undefined,
      allocations:
        allocations.length > 0
          ? allocations.map((a) => {
              const assignedFixtures = fixtureAssignments
                .filter(
                  (fa) =>
                    fa.subcategoryId === a.subcategoryId ||
                    (fa.isBoundary && fa.boundaryNames?.includes(a.subcategoryName)),
                )
                .map((fa) => fa.fixtureId);
              return {
                subcategoryId: a.subcategoryId,
                subcategoryName: a.subcategoryName,
                percentage: a.percentage,
                fixtureIds: assignedFixtures.length > 0 ? assignedFixtures : undefined,
              };
            })
          : undefined,
    });
  }
  return zones;
}

export function buildMacroplanogramName(
  macroplanogramNameInput: string,
  selectedCategory: string,
  selectedProductGroup: string,
  productTaxonomy: PublishZoneMapParams['productTaxonomy'],
  zoneCreationProperties: Map<number, ZoneCreationProperties>,
): string {
  const categoryName =
    productTaxonomy?.categoryList?.find((cat) => cat.categoryId === selectedCategory)?.categoryName || selectedCategory;
  return (
    macroplanogramNameInput.trim() ||
    (categoryName && selectedProductGroup
      ? `${categoryName} ${selectedProductGroup}`
      : Array.from(zoneCreationProperties.values())[0]?.zoneName || 'Unknown')
  );
}

interface UsePublishZoneMapInput extends PublishZoneMapParams {
  initialZoneCreationPropertiesMap: Map<number, ZoneCreationProperties>;
  setIsLoadingPublishZone: (v: boolean) => void;
  setPublishZoneSuccessful: (v: boolean) => void;
  setPublishZoneErrorMsg: (v: string | undefined) => void;
  setOpenConfirmationModal: (v: boolean) => void;
  setZoneCreationProperties: (v: Map<number, ZoneCreationProperties>) => void;
}

export function usePublishZoneMap({
  zoneCreationProperties,
  fixtureAssignments,
  selectedDepartment,
  selectedProductGroup,
  selectedCategory,
  macroplanogramNameInput,
  store,
  timeStamp,
  copyToNewTimestamp,
  switchButtonValue,
  nextGenEnabled,
  productTaxonomy,
  publishZone,
  putZoneMapConfig,
  initialZoneCreationPropertiesMap,
  setIsLoadingPublishZone,
  setPublishZoneSuccessful,
  setPublishZoneErrorMsg,
  setOpenConfirmationModal,
  setZoneCreationProperties,
}: UsePublishZoneMapInput) {
  const config = useProgramConfig();
  const isGfcPublish = config.macroplanogramScope === 'category';

  const onPublishButton = useCallback(async () => {
    setIsLoadingPublishZone(true);

    // GFC: gate on the same Drop Zone validation the edit-time flow uses, so the
    // two layers never disagree. Surface all issues joined into one message.
    if (isGfcPublish) {
      const dropZoneErrors = validateDropZones(zoneCreationProperties);
      if (dropZoneErrors.length > 0) {
        setPublishZoneErrorMsg(dropZoneErrors.join('; '));
        setIsLoadingPublishZone(false);
        return;
      }
    }

    const zonesToUpdate = buildZonesToUpdate(
      zoneCreationProperties,
      fixtureAssignments,
      selectedDepartment,
      isGfcPublish,
    );
    const macroplanogramName = buildMacroplanogramName(
      macroplanogramNameInput,
      selectedCategory,
      selectedProductGroup,
      productTaxonomy,
      zoneCreationProperties,
    );

    try {
      let zoneMapConfigId = null;

      if (nextGenEnabled) {
        const zones = Array.from(zoneCreationProperties.values()).map((zone, index) => ({
          name: zone.zoneName || `Zone-${index + 1}`,
          fixtures: zone.fixtureIds.map((fixtureId) => ({
            id: fixtureId,
            fixtureType: zone.fixtureModels?.get(fixtureId) || 'DEFAULT',
            facings: zone.facings || 0,
          })),
          ...(zone.allocations && {
            allocations: zone.allocations.map((a) => ({
              subcategoryId: a.subcategoryId,
              percentage: a.percentage,
            })),
          }),
        }));

        const putResp = await putZoneMapConfig({
          input: { storeId: store, productGroup: selectedProductGroup, zoneMapConfigName: store, zones },
        });
        zoneMapConfigId = putResp.putZoneMapConfig.zoneMapConfigId;
      }

      const resp = await publishZone({
        input: {
          macroplanogramName,
          storeId: store,
          zoneMap: zonesToUpdate,
          timestamp: switchButtonValue === SwitchButtonValue.CopyToNewMap ? copyToNewTimestamp : timeStamp,
          isDraft: false,
          zoneMapConfigId,
        },
      });

      if (resp.publishZone.error === null) {
        setPublishZoneSuccessful(true);
        setZoneCreationProperties(initialZoneCreationPropertiesMap);
      } else {
        setPublishZoneErrorMsg(resp.publishZone.error?.errorMessage);
      }
    } catch (error) {
      setPublishZoneErrorMsg(error instanceof Error ? error.message : 'Operation failed');
    } finally {
      setOpenConfirmationModal(false);
      setIsLoadingPublishZone(false);
    }
  }, [
    publishZone,
    putZoneMapConfig,
    selectedProductGroup,
    selectedCategory,
    zoneCreationProperties,
    store,
    timeStamp,
    copyToNewTimestamp,
    initialZoneCreationPropertiesMap,
    switchButtonValue,
    nextGenEnabled,
    macroplanogramNameInput,
    fixtureAssignments,
    selectedDepartment,
    productTaxonomy,
    isGfcPublish,
    setIsLoadingPublishZone,
    setPublishZoneSuccessful,
    setPublishZoneErrorMsg,
    setOpenConfirmationModal,
    setZoneCreationProperties,
  ]);

  return { onPublishButton };
}
